import { ChangeEvent, useEffect, useState } from 'react'
import { api, postJSON } from './api'
import { messageFor, Notice } from './ui'
import { SystemLayout } from './SystemAdmins'
import './google-drive-setup.css'

type Drive = { id: string; name: string }
type Setup = {
  configured: boolean; service_account_email: string; project_id: string
  updated_at: string; updated_by: string; drives: Drive[] | null; problem_code: string; problem: string
}
type Substrate = { substrate: string; setup?: Setup; unreachable?: string }
type Listing = { available: boolean; substrates: Substrate[] }

const request = <T,>(path: string, init: RequestInit = {}) => api<T>(path, { ...init, signal: AbortSignal.timeout(60000) })

function when(seconds: string) {
  const value = Number(seconds)
  return value > 0 ? new Date(value * 1000).toLocaleString() : ''
}

export function driveStatus(item: Substrate): { label: string; tone: 'ok' | 'warn' | 'off' } {
  if (item.unreachable) return { label: 'Not reachable', tone: 'warn' }
  const setup = item.setup
  if (!setup?.configured) return { label: 'Not set up', tone: 'off' }
  const drives = setup.drives?.length ?? 0
  if (drives > 0) return { label: drives === 1 ? 'Working · 1 Shared Drive' : `Working · ${drives} Shared Drives`, tone: 'ok' }
  return { label: 'Needs attention', tone: 'warn' }
}

function KeySteps({ email }: { email?: string }) {
  return <ol className="drive-steps">
    <li>In Google Cloud, open <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noreferrer">Service accounts</a>, pick the company’s project and choose <strong>Create service account</strong>. A name like “Drive access” is fine. It needs no project roles.</li>
    <li>Turn on the <a href="https://console.cloud.google.com/apis/library/drive.googleapis.com" target="_blank" rel="noreferrer">Google Drive API</a> for the same project.</li>
    <li>Open the new service account, go to <strong>Keys</strong> → <strong>Add key</strong> → <strong>Create new key</strong> → <strong>JSON</strong>. A file downloads.
      <span className="drive-step-note">If Google says key creation is disabled, your Google organization admin must allow service-account keys for this project (policy “Disable service account key creation”).</span></li>
    <li>Upload that file below. AuthEM passes it to this substrate and does not keep a copy.</li>
    <li>In Google Drive, open the Shared Drive → <strong>Manage members</strong> and add {email ? <strong>{email}</strong> : 'the service account’s email'} as <strong>Manager</strong>.
      <span className="drive-step-note">If Google refuses an outside member, the Google Workspace admin of the company that owns the drive must allow outside members on that Shared Drive.</span></li>
  </ol>
}

function SubstrateCard({ item, onSaved }: { item: Substrate; onSaved: (next: Substrate) => void }) {
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [notice, setNotice] = useState('')
  const status = driveStatus(item)
  const setup = item.setup
  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setBusy(true); setError(''); setNotice('')
    try {
      if (file.size > 32 * 1024) throw Error('That file is too large to be a service-account key. Choose the downloaded JSON key file.')
      const next = await postJSON<Substrate>(`/api/v1/system/google-drive/${encodeURIComponent(item.substrate)}/key`, { key_json: await file.text() })
      onSaved(next)
      setNotice('Key saved and checked with Google.')
    } catch (reason) { setError(messageFor(reason)) }
    finally { setBusy(false) }
  }
  return <article className="section-card drive-card" aria-labelledby={`drive-${item.substrate}`}>
    <header className="drive-card-head">
      <div><p className="eyebrow">Substrate</p><h2 id={`drive-${item.substrate}`}>{item.substrate}</h2></div>
      <span className={`drive-status ${status.tone}`}>{status.label}</span>
    </header>
    {item.unreachable && <Notice tone="error">{item.unreachable}</Notice>}
    {setup?.configured && <dl className="drive-facts">
      <div><dt>Service account</dt><dd><code>{setup.service_account_email}</code> <button className="quiet-button" onClick={() => { void navigator.clipboard?.writeText(setup.service_account_email) }}>Copy</button></dd></div>
      <div><dt>Google Cloud project</dt><dd>{setup.project_id}</dd></div>
      {setup.updated_by && <div><dt>Key uploaded</dt><dd>{[setup.updated_by, when(setup.updated_at)].filter(Boolean).join(' · ')}</dd></div>}
    </dl>}
    {setup?.configured && (setup.drives?.length ?? 0) > 0 && <div className="drive-list"><h3>Shared Drives it can use</h3><ul>{setup.drives!.map(d => <li key={d.id}>{d.name}</li>)}</ul></div>}
    {setup?.configured && setup.problem && <Notice tone="error">{setup.problem}</Notice>}
    {notice && <Notice tone="success">{notice}</Notice>}
    {error && <Notice tone="error">{error}</Notice>}
    {!item.unreachable && <>
      <details className="drive-guide" open={!setup?.configured}>
        <summary>{setup?.configured ? 'How to replace the key or add a Shared Drive' : 'How to set it up'}</summary>
        <KeySteps email={setup?.service_account_email} />
      </details>
      <label className={`button ${setup?.configured ? 'secondary' : 'primary'} drive-upload`} aria-disabled={busy}>
        {busy ? 'Checking with Google…' : setup?.configured ? 'Replace key' : 'Upload key file'}
        <input type="file" accept=".json,application/json" disabled={busy} onChange={event => { void upload(event) }} />
      </label>
    </>}
  </article>
}

export function GoogleDriveSetup() {
  const [listing, setListing] = useState<Listing | null>(null), [error, setError] = useState('')
  const load = async () => {
    setError('')
    try { setListing(await request<Listing>('/api/v1/system/google-drive')) }
    catch (reason) { setError(messageFor(reason)) }
  }
  useEffect(() => { void load() }, [])
  const replace = (next: Substrate) => setListing(current => current && { ...current, substrates: current.substrates.map(s => s.substrate === next.substrate ? next : s) })
  return <SystemLayout active="google-drive"><div className="google-drive-setup">
    <div className="page-heading"><div><h1>Google Drive</h1>
      <p>Each substrate reaches Google Drive through its own Google service account. Add that account to a Shared Drive as Manager and apps on the substrate can read and change everything in that drive.</p></div>
      <button className="button secondary" onClick={() => { void load() }}>Check again</button></div>
    {error && <><Notice tone="error">{error}</Notice><button className="button secondary" onClick={() => { void load() }}>Try again</button></>}
    {!listing && !error && <p role="status">Checking each substrate…</p>}
    {listing && !listing.available && <section className="section-card"><h2>No substrates connected</h2><p>This AuthEM has no Calvin connection, so it cannot reach any substrate. The installation configuration needs a Calvin address and service token.</p></section>}
    {listing?.available && listing.substrates.length === 0 && <section className="section-card"><h2>No substrates available</h2><p>Calvin lists no substrates for AuthEM. Its Calvin service token needs the substrates added to its destinations.</p></section>}
    {listing?.substrates.map(item => <SubstrateCard key={item.substrate} item={item} onSaved={replace} />)}
    <p className="drive-footnote">Drive actions are recorded with who asked for them. Older Google authorizations made by admins are listed under <a href="/system/connections">Previous authorizations</a>.</p>
  </div></SystemLayout>
}
